import { useState } from "react";
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import { stripePromise } from "../../config/stripe";
import RandomQuote from "../../components/RandomQuote/RandomQuote";
import Notification from "../../components/Notification/Notification";
import Spinner from "../../components/Spinner/Spinner";
import BrandTitle from "../../components/BrandTitle/BrandTitle";
import PaymentForm from "../../components/PaymentForm/PaymentForm";
import { createSetupIntentForSignup } from "../../api/stripe/stripe";
import { Trans, useTranslation } from "react-i18next";

function SignupForm() {
  const { t, i18n } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [step, setStep] = useState(1);
  const [accountType, setAccountType] = useState("regular"); // "regular" or "enterprise"
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [cardholderName, setCardholderName] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [billingContact, setBillingContact] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleBillingChange = (e) => {
    setBillingContact({ ...billingContact, [e.target.name]: e.target.value });
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError("");
    }
  };

  const goToNextStep = (e) => {
    e.preventDefault();
    setError("");
    if (!form.firstName || !form.lastName || !form.email || !form.password) {
      setError(t("auth.fillRequiredFields"));
      return;
    }
    if (form.password !== confirmPassword) {
      setError(t("auth.passwordsDoNotMatch"));
      return;
    }
    // Pre-fill billing contact email with user email for enterprise
    if (accountType === "enterprise" && !billingContact.email) {
      setBillingContact({ ...billingContact, email: form.email });
    }
    setStep(2);
  };

  const handleBack = (e) => {
    e.preventDefault();
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!termsAccepted) {
      setError(t("auth.acceptTerms"));
      return;
    }

    setIsLoading(true);

    try {
      if (accountType === "enterprise") {
        // Enterprise signup - no payment required
        if (
          !billingContact.name ||
          !billingContact.email ||
          !billingContact.company
        ) {
          setError(t("auth.enterpriseSignup.fillBillingFields"));
          setIsLoading(false);
          return;
        }

        const payload = {
          ...form,
          language: i18n.resolvedLanguage,
          billingContact,
        };

        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/auth/signup-enterprise`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || t("auth.signupFailed"));

        // Stores the translation key so it follows language changes
        setSuccessMessage("auth.enterpriseSignup.accountCreated");
        setNotification({
          message: t("auth.enterpriseSignup.accountCreatedShort"),
          type: "success",
        });
      } else {
        // Regular signup - requires payment
        if (!stripe || !elements) {
          setError(t("auth.signup.stripeNotReady"));
          setIsLoading(false);
          return;
        }

        if (!cardComplete) {
          setError(t("auth.signup.completeCard"));
          setIsLoading(false);
          return;
        }

        // Step 1: Create SetupIntent for 3D Secure authentication
        console.log("[Signup] Creating SetupIntent...");
        const { clientSecret } = await createSetupIntentForSignup(
          form.email,
          `${form.firstName} ${form.lastName}`,
        );
        console.log("[Signup] SetupIntent created, confirming card setup...");

        // Step 2: Confirm card setup (triggers 3D Secure/MitID if needed)
        const cardElement = elements.getElement(CardNumberElement);
        const { error: stripeError, setupIntent } =
          await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: cardholderName,
                email: form.email,
              },
            },
          });

        if (stripeError) {
          console.error("[Signup] Card setup failed:", stripeError.message);
          throw new Error(stripeError.message);
        }

        console.log(
          "[Signup] Card setup successful, payment method:",
          setupIntent.payment_method,
        );

        // Step 3: Send signup request with authenticated payment method
        const payload = {
          ...form,
          language: i18n.resolvedLanguage,
          payment: {
            paymentMethodId: setupIntent.payment_method,
            cardName: cardholderName,
          },
        };

        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/auth/signup`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || t("auth.signupFailed"));

        setSuccessMessage("auth.signup.accountCreated");
        setNotification({
          message: t("auth.signup.accountCreatedShort"),
          type: "success",
        });
      }

      // Don't redirect - let them read the message and close the tab
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="authentication-view">
      {isLoading && (
        <Spinner
          text={
            accountType === "enterprise"
              ? t("auth.enterpriseSignup.creating")
              : t("auth.signup.creating")
          }
        />
      )}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="left-wrapper">
        <div className="title">
          <BrandTitle fontSize={40} className="brand-title-login" />
        </div>
        <div className="quote">
          <RandomQuote />
        </div>
      </div>

      <div className="right-wrapper">
        {successMessage ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "40px",
              maxWidth: "500px",
              height: "100%",
              margin: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                marginBottom: "30px",
              }}
            >
              <div
                style={{
                  fontSize: "48px",
                  color: accountType === "enterprise" ? "#6c5ce7" : "#10b981",
                }}
              >
                ✓
              </div>
              <div
                className="title"
                style={{
                  margin: 0,
                  color: accountType === "enterprise" ? "#6c5ce7" : "#10b981",
                }}
              >
                {t("auth.success")}
              </div>
            </div>
            {accountType === "enterprise" && (
              <div
                style={{
                  display: "inline-block",
                  padding: "6px 12px",
                  background: "rgba(108, 92, 231, 0.1)",
                  color: "#6c5ce7",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "20px",
                }}
              >
                {t("auth.enterpriseSignup.accountBadge")}
              </div>
            )}
            <p
              style={{
                fontSize: "16px",
                lineHeight: "1.6",
                color: "#ccc",
                marginBottom: "30px",
              }}
            >
              {t(successMessage)}
            </p>
            <p>{t("auth.closeWindow")}</p>

            {!successMessage && (
              <p>
                {t("auth.alreadyHaveAccount")}{" "}
                <a className="link" href="/login">
                  {t("auth.login.link")}
                </a>
              </p>
            )}

            {successMessage && (
              <p style={{ marginTop: "40px" }}>
                <a className="link" href="/login">
                  {t("auth.signup.home")}
                </a>
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="title">
              {step === 1
                ? t("auth.signup.title")
                : accountType === "enterprise"
                  ? t("auth.signup.billingContactTitle")
                  : t("auth.signup.paymentInfoTitle")}
            </div>

            {step === 1 && (
              <form onSubmit={goToNextStep} autoComplete="off">
                {/* Account Type Selection */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "12px",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "var(--main-text-color)",
                    }}
                  >
                    {t("auth.signup.accountType")}
                  </label>
                  <div style={{ display: "flex", gap: "16px" }}>
                    <label
                      style={{
                        flex: 1,
                        padding: "12px 16px",
                        border: `2px solid ${accountType === "regular" ? "var(--blue-button-color)" : "var(--border-color)"}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        background:
                          accountType === "regular"
                            ? "rgba(35, 130, 228, 0.1)"
                            : "transparent",
                      }}
                    >
                      <input
                        type="radio"
                        name="accountType"
                        value="regular"
                        checked={accountType === "regular"}
                        onChange={(e) => setAccountType(e.target.value)}
                        style={{ marginRight: "8px" }}
                      />
                      <span style={{ fontSize: "14px", fontWeight: 500 }}>
                        {t("auth.signup.regular")}
                      </span>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--secondary-text-color)",
                          marginTop: "4px",
                          marginLeft: "24px",
                        }}
                      >
                        {t("auth.signup.subscriptionBilling")}
                      </div>
                    </label>
                    <label
                      style={{
                        flex: 1,
                        padding: "12px 16px",
                        border: `2px solid ${accountType === "enterprise" ? "#6c5ce7" : "var(--border-color)"}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        background:
                          accountType === "enterprise"
                            ? "rgba(108, 92, 231, 0.1)"
                            : "transparent",
                      }}
                    >
                      <input
                        type="radio"
                        name="accountType"
                        value="enterprise"
                        checked={accountType === "enterprise"}
                        onChange={(e) => setAccountType(e.target.value)}
                        style={{ marginRight: "8px" }}
                      />
                      <span style={{ fontSize: "14px", fontWeight: 500 }}>
                        {t("auth.signup.enterprise")}
                      </span>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--secondary-text-color)",
                          marginTop: "4px",
                          marginLeft: "24px",
                        }}
                      >
                        {t("auth.signup.invoiceBilling")}
                      </div>
                    </label>
                  </div>
                </div>

                <div className="auth-names">
                  <input
                    name="firstName"
                    className="flow-form-row"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder={t("auth.firstName")}
                    autoComplete="new-first-name"
                    required
                  />
                  <input
                    name="lastName"
                    className="flow-form-row"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder={t("auth.lastName")}
                    autoComplete="new-last-name"
                    required
                  />
                </div>
                <br />
                <input
                  name="email"
                  className="flow-form"
                  value={form.email}
                  onChange={handleChange}
                  placeholder={t("common.email")}
                  autoComplete="new-email"
                  required
                />
                <br />
                <input
                  name="password"
                  className="flow-form"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder={t("common.password")}
                  autoComplete="new-password"
                  required
                />
                <br />
                <input
                  name="confirmPassword"
                  className="flow-form"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t("auth.confirmPassword")}
                  autoComplete="new-confirm-password"
                  required
                />
                <br />
                {error && step === 1 && (
                  <span
                    style={{
                      color: "var(--error-color)",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "10px",
                    }}
                  >
                    {error}
                  </span>
                )}
                <button type="submit" className="flow-button">
                  {accountType === "enterprise"
                    ? t("auth.signup.nextBillingContact")
                    : t("auth.signup.nextPayment")}
                </button>
              </form>
            )}

            {step === 2 && accountType === "regular" && (
              <form
                onSubmit={handleSubmit}
                autoComplete="off"
                style={{ maxWidth: "440px" }}
              >
                <input
                  name="cardholderName"
                  className="flow-form"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  placeholder={t("auth.signup.cardholderName")}
                />
                <br />

                <PaymentForm onCardChange={handleCardChange} />
                <br />

                {/* Billing explanation and terms */}
                <div
                  style={{
                    marginTop: "24px",
                    marginBottom: "20px",
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#999",
                    maxWidth: "430px",
                  }}
                >
                  <p style={{ margin: "0 0 16px 0" }}>
                    {t("auth.signup.billingExplanation")}
                  </p>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      cursor: "pointer",
                      gap: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      style={{
                        marginTop: "3px",
                        cursor: "pointer",
                        width: "16px",
                        height: "16px",
                        flexShrink: 0,
                      }}
                    />
                    <span>
                      <Trans
                        i18nKey="auth.signup.agreeTerms"
                        components={{
                          terms: (
                            <a
                              href="/legal/terms-of-service"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#6366f1",
                                textDecoration: "underline",
                              }}
                            />
                          ),
                          privacy: (
                            <a
                              href="/legal/privacy-policy"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#6366f1",
                                textDecoration: "underline",
                              }}
                            />
                          ),
                        }}
                      />
                    </span>
                  </label>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "430px",
                  }}
                >
                  <button
                    onClick={handleBack}
                    className="flow-button"
                    style={{
                      background: "#ddd",
                      color: "#000",
                      width: "190px",
                      height: "60px",
                      padding: "10px",
                      boxSizing: "border-box",
                    }}
                  >
                    {t("common.back")}
                  </button>
                  <button
                    type="submit"
                    className="flow-button"
                    disabled={!termsAccepted}
                    style={{
                      width: "190px",
                      height: "60px",
                      padding: "10px",
                      boxSizing: "border-box",
                      opacity: termsAccepted ? 1 : 0.5,
                      cursor: termsAccepted ? "pointer" : "not-allowed",
                    }}
                  >
                    {t("auth.signup.createAccount")}
                  </button>
                </div>
              </form>
            )}

            {step === 2 && accountType === "enterprise" && (
              <form onSubmit={handleSubmit} autoComplete="off">
                <div
                  style={{
                    marginBottom: "20px",
                    fontSize: "14px",
                    color: "#999",
                  }}
                >
                  <p style={{ margin: "0 0 10px 0" }}>
                    <strong>
                      {t("auth.enterpriseSignup.billingInfoTitle")}
                    </strong>
                  </p>
                  <p style={{ margin: 0 }}>
                    {t("auth.enterpriseSignup.billingInfoText")}
                  </p>
                </div>

                <input
                  name="company"
                  className="flow-form"
                  value={billingContact.company}
                  onChange={handleBillingChange}
                  placeholder={t("auth.enterpriseSignup.company")}
                  required
                />
                <br />
                <input
                  name="name"
                  className="flow-form"
                  value={billingContact.name}
                  onChange={handleBillingChange}
                  placeholder={t("auth.enterpriseSignup.contactName")}
                  required
                />
                <br />
                <input
                  name="email"
                  className="flow-form"
                  type="email"
                  value={billingContact.email}
                  onChange={handleBillingChange}
                  placeholder={t("auth.enterpriseSignup.contactEmail")}
                  required
                />
                <br />
                <input
                  name="phone"
                  className="flow-form"
                  type="tel"
                  value={billingContact.phone}
                  onChange={handleBillingChange}
                  placeholder={t("auth.enterpriseSignup.contactPhone")}
                />
                <br />

                {/* Terms and conditions */}
                <div
                  style={{
                    marginTop: "24px",
                    marginBottom: "20px",
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#999",
                  }}
                >
                  <p style={{ margin: "0 0 16px 0" }}>
                    <Trans
                      i18nKey="auth.enterpriseSignup.billingTerms"
                      components={{ bold: <strong /> }}
                    />
                  </p>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      cursor: "pointer",
                      gap: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      style={{
                        marginTop: "3px",
                        cursor: "pointer",
                        width: "16px",
                        height: "16px",
                        flexShrink: 0,
                      }}
                    />
                    <span>
                      <Trans
                        i18nKey="auth.enterpriseSignup.agreeTerms"
                        components={{
                          terms: (
                            <a
                              href="/legal/terms-of-service"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#6c5ce7",
                                textDecoration: "underline",
                              }}
                            />
                          ),
                          privacy: (
                            <a
                              href="/legal/privacy-policy"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#6c5ce7",
                                textDecoration: "underline",
                              }}
                            />
                          ),
                        }}
                      />
                    </span>
                  </label>
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flow-button"
                    style={{
                      background: "transparent",
                      border: "1px solid var(--border-color)",
                      color: "var(--main-text-color)",
                    }}
                  >
                    {t("common.back")}
                  </button>
                  <button
                    type="submit"
                    className="flow-button"
                    disabled={!termsAccepted}
                    style={{
                      flex: 1,
                      background: "#6c5ce7",
                      opacity: termsAccepted ? 1 : 0.5,
                      cursor: termsAccepted ? "pointer" : "not-allowed",
                    }}
                  >
                    {t("auth.enterpriseSignup.submit")}
                  </button>
                </div>
              </form>
            )}

            {error && step === 2 && (
              <span
                style={{
                  color: "var(--error-color)",
                  marginTop: "10px",
                  marginBottom: "-10px",
                  display: "block",
                }}
              >
                {error}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Wrap SignupForm with Stripe Elements provider
export default function SignupPage() {
  console.log("[SignupPage] stripePromise:", stripePromise);

  return (
    <Elements stripe={stripePromise}>
      <SignupForm />
    </Elements>
  );
}
